import { Box, SxProps, Theme, useTheme } from "@mui/material";
import { useRef, useState } from "react";
import Quill, { EDITOR_CLASS } from "@/components/common/quill/quill";
import { TEXT_PRIMARY_DARK, TEXT_SECONDARY } from "@/config/mui/colorPalette";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import ShowMoreButton from "../showMoreButton/showMoreButton";

export const ReadOnlyQuill = ({
  value,
  isParagraph = false,
  sx,
}: {
  value: string;
  isParagraph?: boolean;
  sx?: SxProps<Theme>;
}) => {
  const isMobile = useMoxieMediaQuery("ipad-ver", "down");

  const defaultStyles = {
    color: TEXT_SECONDARY,
    ...(isMobile
      ? {
          fontSize: "labelSmall.fontSize",
          lineHeight: "labelSmall.lineHeight",
        }
      : {
          fontSize: "labelTiny.fontSize",
          lineHeight: "labelTiny.lineHeight",
        }),
  };

  const paragraphStyles = {
    color: TEXT_PRIMARY_DARK,
    fontSize: "paragraph.fontSize",
    lineHeight: "paragraph.lineHeight",
  };

  return (
    <Quill
      readOnly
      disableToolbar
      value={value}
      sx={{
        [EDITOR_CLASS]: {
          color: TEXT_SECONDARY,
          ...(isParagraph ? paragraphStyles : defaultStyles),
        },
        ...sx,
      }}
    />
  );
};

enum State {
  EXPANDED = "EXPANDED",
  TRUNCATED = "TRUNCATED",
  INIT = "INIT",
}

const MAX_HEIGHT = 90;

export const TruncatedReadOnlyQuill = ({
  value,
  isParagraph = false,
  sx,
}: {
  value: string;
  isParagraph?: boolean;
  sx?: SxProps<Theme>;
}) => {
  const quillWrapperRef = useRef<HTMLDivElement>(null);
  const isMobile = useMoxieMediaQuery("ipad-ver", "down");
  const [truncationState, setTruncationState] = useState<State>(State.INIT);
  const theme = useTheme();

  const enableTruncationWhenOverflow = () => {
    // Guard against multiple checks
    if (truncationState !== State.INIT) return;

    const wrapperElement = quillWrapperRef.current;
    if (!wrapperElement) return;

    // Find the editor element which contains the actual content
    const editorElement = wrapperElement.querySelector(
      ".ql-editor"
    ) as HTMLElement;
    if (!editorElement) return;

    const hasOverflow = editorElement.scrollHeight > MAX_HEIGHT;

    if (hasOverflow) {
      setTruncationState(State.TRUNCATED);
    }
  };

  const defaultStyles = {
    color: TEXT_SECONDARY,
    ...(isMobile
      ? {
          fontSize: "labelSmall.fontSize",
          lineHeight: "labelSmall.lineHeight",
        }
      : {
          fontSize: "labelTiny.fontSize",
          lineHeight: "labelTiny.lineHeight",
        }),
  };

  const paragraphStyles = {
    color: TEXT_PRIMARY_DARK,
    fontSize: "paragraph.fontSize",
    lineHeight: "paragraph.lineHeight",
  };

  const isContentTruncated = truncationState === State.TRUNCATED;
  const isContentExpanded = truncationState === State.EXPANDED;

  return (
    <>
      <Box
        ref={quillWrapperRef}
        sx={{
          overflow: "hidden",
          ...(isContentTruncated && {
            maxHeight: `${MAX_HEIGHT}px`, // using maxHeight for cross-browser consistency (Chrome/Safari)
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
          }),
          transition: theme.transitions.create(["max-height"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shortest,
          }),
        }}
      >
        <Quill
          readOnly
          disableToolbar
          value={value}
          sx={{
            [EDITOR_CLASS]: {
              color: TEXT_SECONDARY,
              ...(isParagraph ? paragraphStyles : defaultStyles),
            },
            ...sx,
          }}
          afterSetValue={enableTruncationWhenOverflow}
        />
      </Box>
      {[State.TRUNCATED, State.EXPANDED].includes(truncationState) && (
        <ShowMoreButton
          isExpanded={isContentExpanded}
          size="small"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setTruncationState(
              isContentTruncated ? State.EXPANDED : State.TRUNCATED
            );
          }}
          sx={{ pl: 0, mt: 0.5 }}
        />
      )}
    </>
  );
};
